/* eslint-disable jsdoc/require-jsdoc, component-structure/single-component-per-file -- Private position row remains colocated with its card. */
import React, { memo } from "react";
import { Box } from "@/components/atoms/Box";
import { Dot } from "@/components/atoms/Dot";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { StatusPill, type StatusPillTone } from "@/components/atoms/StatusPill";
import { Text } from "@/components/atoms/Text";
import PitchPositionPlayerLine, {
  type PitchMemberRemoveState,
} from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import {
  PITCH_NAME_LINK_FOCUS_CLASS,
  PITCH_NAME_LINK_TEXT_CLASS,
} from "@/features/scouting/constants/pitchNameLink";
import type {
  TacticalSlot,
  ScoutingGetWatchlistPitchQuery,
} from "@/generated/graphql";
import type { SquadOverlayPlayer } from "@/features/scouting/utils/watchlistPitchOverlay";
export type Member = NonNullable<
  ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]
>["members"]["edges"][number]["node"];
/** A departing squad player's action (SE-8390); absent = staying. */
export type SquadPlayerDeparture = "Sell" | "Loan";
/**
 * A ● squad player as the card draws it: the overlay's shape plus the
 * departure tag the scenario projection sets (the watchlist never does).
 */
export type PitchSquadPlayer = SquadOverlayPlayer & {
  readonly departure?: SquadPlayerDeparture;
};
/** Pill tone per departure, from the SE-8390 frame (939:16121). */
const DEPARTURE_TONE: Readonly<Record<SquadPlayerDeparture, StatusPillTone>> = {
  Sell: "warning",
  Loan: "neutral",
};
const formatGpr = (gpr: number | null): string =>
  gpr === null ? "—" : Math.round(gpr).toString();
// A player line labels an unknown rating (SE-8414); a known one is bare. The
// Figma line (1010:20293) draws no rating; SE-8414 AC1 keeps it — the data
// rule outranks the frame (source precedence).
const formatLineGpr = (gpr: number | null): string =>
  gpr === null ? "GPR —" : formatGpr(gpr);
/** The slot's mean over rated players, and how many it left out as unrated. */
interface SlotGpr {
  readonly mean: number | null;
  readonly unrated: number;
}
const averageGpr = (
  players: readonly (Member | PitchSquadPlayer)[]
): SlotGpr => {
  // A departing player is leaving the slot: his GPR is not the slot's.
  const staying = players.filter(
    player => !("departure" in player && player.departure)
  );
  const ratings = staying.flatMap(player =>
    typeof player.gpr === "number" ? [player.gpr] : []
  );
  return {
    mean:
      ratings.length > 0
        ? ratings.reduce((total, rating) => total + rating, 0) / ratings.length
        : null,
    unrated: staying.length - ratings.length,
  };
};
// "Avg. GPR 80 · 1 unrated" (SE-8414, frame 944:16283): the mean over rated
// players, then the count it left out; "Avg. GPR —" when nobody is rated.
const badgeText = ({ mean, unrated }: SlotGpr): string =>
  mean !== null && unrated > 0
    ? `Avg. GPR ${formatGpr(mean)} · ${unrated} unrated`
    : `Avg. GPR ${formatGpr(mean)}`;
/**
 * Lines a card shows before `Show all N` (SE-8486 D1, frame
 * `molecules/PitchPositionCard`): squad ● lines first, then targets.
 */
export const PITCH_CARD_LINE_CAP = 3;
/**
 * SE-8535: the compact card's cap, below the 570 px reference field — a
 * shorter card stops colliding with the next formation row.
 */
export const PITCH_CARD_COMPACT_LINE_CAP = 2;
/**
 * The lines the card draws: every one when expanded, else the first `cap`.
 * @param squadPlayers - The ● squad players, drawn first
 * @param players - The targets, after the squad
 * @param expanded - Whether the inline Show all is open
 * @param cap - Lines the card shows before Show all
 * @returns The squad and target lines to draw
 */
const visibleLines = (
  squadPlayers: readonly PitchSquadPlayer[],
  players: readonly Member[],
  expanded: boolean,
  cap: number
): {
  readonly squad: readonly PitchSquadPlayer[];
  readonly targets: readonly Member[];
} =>
  expanded
    ? { squad: squadPlayers, targets: players }
    : {
        squad: squadPlayers.slice(0, cap),
        targets: players.slice(0, Math.max(0, cap - squadPlayers.length)),
      };
/**
 * The card header: the slot code and, unless the slot is empty, the Avg. GPR
 * badge (the frame hides it and line 1 reads "No targets").
 * @param slot - The card's slot
 * @param players - The targets
 * @param squadPlayers - The ● squad players
 * @returns The header's children
 */
const renderHeader = (
  slot: TacticalSlot,
  players: readonly Member[],
  squadPlayers: readonly PitchSquadPlayer[]
) => (
  <>
    <Text variant="caption-strong" className="text-content-primary">
      {slot}
    </Text>
    {players.length === 0 && squadPlayers.length === 0 ? null : (
      <Box className="items-center bg-surface-subtle px-2 py-1">
        <Text variant="micro" className="text-content-secondary">
          {badgeText(averageGpr([...players, ...squadPlayers]))}
        </Text>
      </Box>
    )}
  </>
);

/** Prepared "Show all" affordance rendered under the ranked rows. */
export interface ShowAllAction {
  readonly label: string;
  readonly accessibilityLabel: string;
  /** True when the press expands inline; false when it opens the pop-out. */
  readonly isToggle: boolean;
  readonly onPress: () => void;
}
const NOOP_PLAYER_ASSIGN = (
  _playerId: string,
  _slot: TacticalSlot | null
) => {};

const NO_SQUAD: readonly PitchSquadPlayer[] = [];
const NO_REMOVALS: Readonly<Record<string, PitchMemberRemoveState>> = {};

const PitchPositionNodeView = ({
  slot,
  players,
  squadPlayers = NO_SQUAD,
  expanded,
  canAssign = false,
  nodeRef,
  isDropTarget = false,
  assignmentSlots = [],
  onAssign,
  showAllAction,
  onRemoveMember,
  removeStateById = NO_REMOVALS,
  isWeakest = false,
  onOpenCard,
  openSquadPlayer,
  // Computed once per render from the three props above (a default may read
  // earlier destructured params); the View keeps its expression body.
  lineCap = PITCH_CARD_LINE_CAP,
  lines = visibleLines(squadPlayers, players, expanded, lineCap),
}: {
  /** The lines to draw; derived from the players unless a test supplies it. */
  lines?: ReturnType<typeof visibleLines>;
  expanded: boolean;
  /** Prepared "Show all" affordance: the pop-out or the inline expansion. */
  showAllAction: ShowAllAction | null;
  slot: TacticalSlot;
  players: readonly Member[];
  /**
   * Read-only current-squad players drawn in this slot (SE-8317); a departing
   * one carries `departure` and stays in place, tagged (SE-8390).
   */
  squadPlayers?: readonly PitchSquadPlayer[];
  canAssign?: boolean;
  nodeRef?: (node: HTMLElement | null) => void;
  isDropTarget?: boolean;
  assignmentSlots?: readonly TacticalSlot[];
  onAssign?: (playerId: string, slot: TacticalSlot | null) => void;
  /** Removes a member from the scenario (SE-8353); absent = no affordance. */
  onRemoveMember?: (playerId: string) => void;
  /** Where each member's removal stands (SE-8353); idle when absent. */
  removeStateById?: Readonly<Record<string, PitchMemberRemoveState>>;
  /** One of the three weakest positions: the card border is dashed. */
  isWeakest?: boolean;
  /**
   * SE-8535 (PRD §4.6): opens the slot's pop-out from the card header —
   * absent on an empty slot and where no pop-out is wired.
   */
  onOpenCard?: () => void;
  /**
   * SE-8605: per ● squad player id, opens that player's page from the name;
   * absent = plain text. The ○ lines bind the same navigator themselves.
   */
  openSquadPlayer?: Readonly<Record<string, () => void>>;
  /** Lines shown before Show all; compact cards show fewer (SE-8535). */
  lineCap?: number;
}) => (
  <Box
    ref={nodeRef as never}
    testID={`watchlist-pitch-slot-${slot}`}
    accessibilityLabel={
      canAssign ? `${slot} position assignment target` : `${slot} position`
    }
    // Figma 1011:21548 / component 1010:20306: a solid 1px outline-default
    // border; dashed ONLY for `weakest` (one of the three weakest positions).
    // The old rule dashed any slot with ≤1 player, which the design never had.
    className={`flex w-pitch-position-node flex-col items-start gap-1 rounded-md border border-outline-default bg-surface-base p-2 ${
      isDropTarget ? "ring-2 ring-accent-primary" : ""
    } ${isWeakest ? "border-dashed" : ""}`}
  >
    {onOpenCard ? (
      <Pressable
        testID={`watchlist-pitch-slot-open-${slot}`}
        accessibilityRole="button"
        accessibilityLabel={`Open ${slot}`}
        onPress={onOpenCard}
        className="w-full flex-row items-center gap-1 overflow-hidden rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default"
      >
        {renderHeader(slot, players, squadPlayers)}
      </Pressable>
    ) : (
      <Stack
        direction="horizontal"
        className="w-full items-center gap-1 overflow-hidden"
      >
        {renderHeader(slot, players, squadPlayers)}
      </Stack>
    )}
    {players.length === 0 && squadPlayers.length === 0 ? (
      <Text variant="caption" className="text-content-muted">
        No targets
      </Text>
    ) : null}
    {lines.squad.map((player, index) => (
      <Stack
        key={player.id}
        testID={`pitch-squad-player-${player.id}`}
        direction="horizontal"
        className="w-full items-center gap-1 overflow-hidden"
      >
        {/* PitchPlayerLine kind=squad: ● accent (Figma 1010:20293). */}
        <Dot
          testID={`pitch-squad-marker-${player.id}`}
          className="bg-accent-primary"
        />
        {openSquadPlayer?.[player.id] ? (
          <Pressable
            testID={`pitch-name-link-squad-${player.id}`}
            accessibilityRole="link"
            onPress={openSquadPlayer[player.id]}
            className={`min-w-0 flex-1 ${PITCH_NAME_LINK_FOCUS_CLASS}`}
          >
            <Text
              variant={index === 0 ? "caption-strong" : "caption"}
              className={`${
                player.departure
                  ? "text-content-subtle"
                  : "text-content-primary"
              } ${PITCH_NAME_LINK_TEXT_CLASS}`}
              isTruncated
            >
              {player.displayName}
            </Text>
          </Pressable>
        ) : (
          <Text
            // rank=first — the slot's first line — is bold.
            variant={index === 0 ? "caption-strong" : "caption"}
            className={
              player.departure
                ? "min-w-0 flex-1 text-content-subtle"
                : "min-w-0 flex-1 text-content-primary"
            }
            isTruncated
          >
            {player.displayName}
          </Text>
        )}
        {player.departure ? (
          <StatusPill
            testID={`pitch-departure-pill-${player.id}`}
            tone={DEPARTURE_TONE[player.departure]}
            label={player.departure}
          />
        ) : null}
        <Text variant="micro" className="text-content-muted">
          {formatLineGpr(player.gpr ?? null)}
        </Text>
      </Stack>
    ))}
    <Stack
      testID={`pitch-slot-lines-${slot}`}
      className="w-full items-start gap-1 overflow-hidden"
    >
      {lines.targets.map((player, index) => (
        <PitchPositionPlayerLine
          key={player.id}
          player={player}
          // rank=first is the slot's first line: a target only when no ●
          // squad line sits above it.
          isPrimary={index === 0 && lines.squad.length === 0}
          slot={slot}
          canAssign={canAssign && Boolean(onAssign)}
          assignmentSlots={assignmentSlots}
          onAssign={onAssign ?? NOOP_PLAYER_ASSIGN}
          onRemove={onRemoveMember}
          removeState={removeStateById[player.playerId]}
        />
      ))}
    </Stack>
    {showAllAction ? (
      <Pressable
        testID={`watchlist-pitch-show-all-${slot}`}
        accessibilityRole="button"
        accessibilityLabel={showAllAction.accessibilityLabel}
        accessibilityState={showAllAction.isToggle ? { expanded } : undefined}
        onPress={showAllAction.onPress}
        className="self-start rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default"
      >
        <Text variant="caption-strong" className="text-accent-primary">
          {showAllAction.label}
        </Text>
      </Pressable>
    ) : null}
  </Box>
);

PitchPositionNodeView.displayName = "PitchPositionNodeView";
export default memo(PitchPositionNodeView);
/* eslint-enable jsdoc/require-jsdoc, component-structure/single-component-per-file -- End private card helper scope. */
